import { useEffect, useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Plus, Trash2 } from 'lucide-react';
import {
    updateContact,
    deleteContact,
    addContact
} from '@/api/contacts';
import { fetchOrganizationById } from '@/api/organizations';

const EditContactsModal = ({ isOpen, onClose, organization, setOrganizations }) => {
    const [localContacts, setLocalContacts] = useState([]);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (organization?.id) {
            fetchOrganizationById(organization.id).then((org) => {
                setLocalContacts(org.contacts || []);
            });
        }
    }, [organization]);

    const updateContactField = (contactId, field, value) => {
        setLocalContacts((prev) =>
            prev.map((contact) =>
                contact.id === contactId ? { ...contact, [field]: value } : contact
            )
        );
    };

    const handleAddContact = () => {
        setLocalContacts((prev) => [
            ...prev,
            {
                id: `temp-${Date.now()}`, // ← временный уникальный ID
                full_name: '',
                email: '',
                phone: '',
                title: '',
                isNew: true
            }
        ]);
    };

    const handleRemoveContact = async (contact) => {
        if (contact.isNew || !contact.id) {
            // Удаляем просто с фронта
            setLocalContacts((prev) =>
                prev.filter((c) => c !== contact)
            );
            return;
        }

        try {
            await deleteContact(contact.id);
            setLocalContacts((prev) =>
                prev.filter((c) => c.id !== contact.id)
            );
            // Можно также обновить setOrganizations, если нужно
        } catch (err) {
            console.error('Failed to delete contact:', err);
        }
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            for (const contact of localContacts) {
                if (contact.isNew) {
                    await addContact(organization.id, {
                        full_name: contact.full_name,
                        email: contact.email,
                        phone: contact.phone,
                        title: contact.title
                    });
                } else {
                    await updateContact(contact.id, contact);
                }
            }

            const updatedOrg = await fetchOrganizationById(organization.id);
            setOrganizations((prev) =>
                prev.map((org) => (org.id === updatedOrg.id ? updatedOrg : org))
            );
            onClose();
        } catch (err) {
            console.error('Failed to save contacts:', err);
        } finally {
            setIsSaving(false);
        }
    };
    if (!organization) return null;

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-2xl" style={{ zIndex: 1000 }}>
                <DialogHeader>
                    <DialogTitle>Edit Organization Contacts</DialogTitle>
                    <DialogDescription>
                        Manage contacts for <strong>{organization.name}</strong>
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    <div className="flex justify-between items-center">
                        <Label>Contacts</Label>
                        <Button variant="outline" size="sm" onClick={handleAddContact}>
                            <Plus className="mr-2 h-4 w-4" />
                            Add Contact
                        </Button>
                    </div>

                    <div className="space-y-3 max-h-[400px] overflow-y-auto custom-scrollbar">
                        {localContacts.map((contact, index) => (
                            <Card key={contact.id} className="p-3">
                                <div className="flex justify-between items-start mb-3">
                                    <h5 className="font-medium">Contact {index + 1}</h5>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleRemoveContact(contact)}
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label className="text-xs mb-3">Name</Label>
                                        <Input
                                            value={contact.full_name || ''}
                                            onChange={(e) =>
                                                updateContactField(contact.id, 'full_name', e.target.value)
                                            }
                                            placeholder="Contact name"
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-xs mb-3">Title</Label>
                                        <Input
                                            value={contact.title || ''}
                                            onChange={(e) =>
                                                updateContactField(contact.id, 'title', e.target.value)
                                            }
                                            placeholder="Job title"
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-xs mb-3">Email</Label>
                                        <Input
                                            type="email"
                                            value={contact.email || ''}
                                            onChange={(e) =>
                                                updateContactField(contact.id, 'email', e.target.value)
                                            }
                                            placeholder="email@example.com"
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-xs mb-3">Phone (Optional)</Label>
                                        <Input
                                            value={contact.phone || ''}
                                            onChange={(e) =>
                                                updateContactField(contact.id, 'phone', e.target.value)
                                            }
                                            placeholder="(555) 123-4567"
                                        />
                                    </div>
                                </div>
                            </Card>
                        ))}
                    </div>
                </div>

                <div className="flex justify-end space-x-2 pt-4">
                    <Button variant="outline" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button onClick={handleSave} disabled={isSaving}>
                        {isSaving ? 'Saving...' : 'Save'}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default EditContactsModal;